// FileSummary.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatFileSize } from 'utilities/Files';

// CSS
import './FileSummary.css';

// Icons
import ArchiveIcon from 'svg/ArchiveIcon';
import BinaryIcon from 'svg/BinaryIcon';
import GenericIcon from 'svg/GenericIcon';
import Icon from 'svg/Icon';

/**
 * FileSummary
 *
 * Displays a summary of a file node, including author, title, file info, and date.
 *
 * @param {object} node - The file node object.
 * @param {Function} onClick - Click handler for the summary.
 * @param {Boolean} focused - Whether the summary is focused.
 *
 * @returns {ReactNode} FileSummary component.
 */
const FileSummary = ({ node, onClick, focused }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  // Hover State

  const [hovered, setHovered] = useState(false);

  const userName = node?.user?.name || 'Anonymous';

  // Get file title from node title or filename
  const fileTitle = node?.title || node?.data_file?.filename || node?.data_document?.filename || 'Untitled File';

  // Get file metadata
  const fileSize = node?.data_file?.size ?? node?.data_document?.size;
  const rawExtension = node?.data_file?.extension || node?.data_document?.extension || node?.fileName?.split('.')?.pop();
  const fileExtension = rawExtension ? rawExtension.toUpperCase() : undefined;
  const mimeType = (node?.data_file?.mimeType || node?.data_document?.mimeType || '').toLowerCase();

  // ----- MEMOS ----- //

  /**
   * Formatted Size
   *
   * Formats the file size into a human-readable string.
   * E.g., "1.2 MB"
   *
   * @returns {String} Formatted file size.
   */

  const formattedSize = useMemo(() => formatFileSize(fileSize), [fileSize]);

  /**
   * File Info
   *
   * Combines file extension and formatted size into a single string.
   * E.g., "ZIP • 1.2 MB"
   *
   * @returns {String} File info string.
   */
  const fileInfo = useMemo(() => {
    const parts = [];
    if (fileExtension) parts.push(fileExtension);
    if (formattedSize) parts.push(formattedSize);
    return parts.join(' • ');
  }, [fileExtension, formattedSize]);

  const IconComponent = useMemo(() => {
    const ext = (rawExtension || '').toLowerCase();

    const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz', 'gzip', 'tgz', 'bz', 'bz2', 'xz'];
    if (archiveExts.includes(ext) || mimeType.includes('zip') || mimeType.includes('tar') || mimeType.includes('compress') || mimeType.includes('archive')) {
      return ArchiveIcon;
    }

    const binaryExts = ['exe', 'msi', 'bin', 'dmg', 'pkg', 'app', 'ipa', 'apk', 'iso'];
    if (binaryExts.includes(ext) || mimeType === 'application/octet-stream' || mimeType.includes('msdownload') || mimeType.includes('x-msi') || mimeType.includes('x-msdownload')) {
      return BinaryIcon;
    }

    return GenericIcon;
  }, [rawExtension, mimeType]);

  if (!node) return null;

  // ----- RENDER ----- //

  return (

      <div
        className={`file-summary-container${focused ? ' focused' : ''}`}
        style={{
          backgroundColor: hovered ? theme.backgroundHover : undefined,
        }}
        role="button"
        tabIndex={0}
        aria-label={`${userName} uploaded ${fileTitle}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >

        {/* LEFT SECTION */}
        <div className="file-summary-left">

          {/* FILE ICON */}
          <span className="file-summary-file-icon" aria-hidden="true">
            <IconComponent width={40} height={40} />
          </span>

        </div>

        {/* CENTER */}
        <div className="file-summary-center">

          {/* CENTER TOP */}
          <div className="file-summary-center-top">

            {/* TITLE */}
            <div
              className="file-summary-title"
            >{fileTitle}</div>

          </div>

          {/* CENTER BOTTOM */}
          <div className="file-summary-center-bottom">

            {/* AUTHOR */}
            <span
              className="file-summary-author"
            >{userName}</span>

            {/* FILE INFO */}
            {fileInfo && (
              <span
                className="file-summary-file-info"
              > • {fileInfo}</span>
            )}

          </div>
        </div>

        {/* OPEN */}
        <div
          className="file-summary-open"
          title="Open"
        >
          <Icon
            iconKey="chevron_right"
            color={theme.foreground}
            width={16}
            height={16}
          />
        </div>

      </div>
  );
};

// Dev Tools
FileSummary.displayName = 'FileSummary';

export default FileSummary;
